import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  // Check if user is logged in (from localStorage)
  const isLoggedIn = localStorage.getItem('currentUser');
  const currentUser = isLoggedIn ? JSON.parse(isLoggedIn) : null;

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    navigate('/');
    window.location.reload();
  };

  return (
    <header className="absolute top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_elementor-site-1/artifacts/ezkys4rp_df349819-de0d-4936-a5a7-6cc61c7e7cb0.png" 
            alt="WildCamping Logo" 
            className="h-16 sm:h-24 md:h-32 lg:h-40 xl:h-48 w-auto object-contain wc-logo-firelight"
            style={{
              // Logo PNG has solid black background. Using `mix-blend-mode: lighten`
              // makes the black pixels invisible against the dark page background,
              // so only the lighter badge elements (green, cream, orange) show.
              mixBlendMode: 'lighten',
            }}
          />
        </Link>

        {/* Mobile compact nav – 2 most important links visible on phone */}
        <nav className="flex lg:hidden items-center gap-1 text-xs sm:text-sm" data-testid="mobile-compact-nav">
          <Link
            to="/locations"
            className="text-white hover:text-orange-400 transition-colors font-semibold px-2 py-1.5 rounded-md hover:bg-white/10 whitespace-nowrap"
            data-testid="mobile-nav-locations"
          >
            Místa
          </Link>
          <Link
            to="/add-location"
            className="text-white hover:text-orange-400 transition-colors font-semibold px-2 py-1.5 rounded-md hover:bg-white/10 whitespace-nowrap"
            data-testid="mobile-nav-add"
          >
            Přidat
          </Link>
        </nav>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/locations" className="text-white hover:text-orange-400 transition-colors font-medium">
            Místa
          </Link>
          <Link to="/add-location" className="text-white hover:text-orange-400 transition-colors font-medium">
            Přidať místo
          </Link>
          <Link to="/how-it-works" className="text-white hover:text-orange-400 transition-colors font-medium">
            Jak to funguje
          </Link>
          <Link to="/about" className="text-white hover:text-orange-400 transition-colors font-medium">
            O projektu
          </Link>
          <Link to="/contact" className="text-white hover:text-orange-400 transition-colors font-medium">
            Kontakt
          </Link>
        </nav>

        {/* Auth Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              {currentUser?.is_admin && (
                <Link to="/admin">
                  <Button variant="ghost" className="text-white hover:bg-red-600/30 border border-red-400/40" data-testid="admin-link">
                    🛡️ Admin
                  </Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="ghost" className="text-white hover:bg-white/20 border border-white/30">
                  <User className="w-4 h-4 mr-2" />
                  {currentUser?.name}
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Odhlásit
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/20 border border-white/30">
                  Přihlásit
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                  Registrovat
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden text-white p-2 hover:bg-white/20 rounded-lg transition-all"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-md border-t border-white/10">
          <nav className="flex flex-col p-6 gap-4">
            <Link 
              to="/locations" 
              className="text-white hover:text-orange-400 transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Místa
            </Link>
            <Link 
              to="/add-location" 
              className="text-white hover:text-orange-400 transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Přidať místo
            </Link>
            <Link 
              to="/how-it-works" 
              className="text-white hover:text-orange-400 transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Jak to funguje
            </Link>
            <Link 
              to="/about" 
              className="text-white hover:text-orange-400 transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              O projektu
            </Link>
            <Link 
              to="/contact" 
              className="text-white hover:text-orange-400 transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Kontakt
            </Link>
            <div className="border-t border-white/10 my-2"></div>
            {isLoggedIn ? (
              <>
                {currentUser?.is_admin && (
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start text-white hover:bg-red-600/30 border border-red-400/40" data-testid="mobile-admin-link">
                      🛡️ Admin
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full text-white justify-start">
                    <User className="w-4 h-4 mr-2" />
                    {currentUser?.name}
                  </Button>
                </Link>
                <Button 
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Odhlásit
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full text-white justify-start">
                    Přihlásit
                  </Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                    Registrovat
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;